/**
 * PCG32 (pcg_basic, XSH-RR 64/32). The only source of randomness allowed in sim/gen/meta.
 *
 * The 64-bit state is held as two unsigned 32-bit halves so results are bit-identical on
 * every JS engine and the state serialises as plain JSON numbers (no BigInt).
 */

export interface Pcg32State {
  stateHi: number;
  stateLo: number;
  incHi: number;
  incLo: number;
}

// 6364136223846793005 = 0x5851f42d_4c957f2d
const MUL_HI = 0x5851f42d;
const MUL_LO = 0x4c957f2d;
const TWO_32 = 4294967296;

/** Low and high 32 bits of the 64-bit product of two unsigned 32-bit integers. */
function mul32x32(a: number, b: number): [lo: number, hi: number] {
  const a0 = a & 0xffff;
  const a1 = a >>> 16;
  const b0 = b & 0xffff;
  const b1 = b >>> 16;
  const p00 = a0 * b0;
  const p01 = a0 * b1;
  const p10 = a1 * b0;
  const p11 = a1 * b1;
  // All partial sums stay below 2^53, so plain number arithmetic is exact here.
  const mid = p01 + p10 + Math.floor(p00 / 0x10000);
  const lo = ((mid & 0xffff) * 0x10000 + (p00 & 0xffff)) >>> 0;
  const hi = (p11 + Math.floor(mid / 0x10000)) >>> 0;
  return [lo, hi];
}

function fnv1a(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export class Pcg32 {
  private stateHi = 0;
  private stateLo = 0;
  private incHi = 0;
  private incLo = 0;

  /** `seed` and `stream` are taken as unsigned 32-bit integers. */
  constructor(seed: number, stream = 1) {
    const seq = stream >>> 0;
    this.incHi = seq >>> 31;
    this.incLo = ((seq << 1) | 1) >>> 0;
    this.nextU32();
    const sum = this.stateLo + (seed >>> 0);
    this.stateLo = sum >>> 0;
    this.stateHi = (this.stateHi + (sum >= TWO_32 ? 1 : 0)) >>> 0;
    this.nextU32();
  }

  static fromState(s: Pcg32State): Pcg32 {
    const rng = new Pcg32(0, 0);
    rng.stateHi = s.stateHi >>> 0;
    rng.stateLo = s.stateLo >>> 0;
    rng.incHi = s.incHi >>> 0;
    rng.incLo = s.incLo >>> 0;
    return rng;
  }

  getState(): Pcg32State {
    return {
      stateHi: this.stateHi,
      stateLo: this.stateLo,
      incHi: this.incHi,
      incLo: this.incLo,
    };
  }

  nextU32(): number {
    const oldHi = this.stateHi;
    const oldLo = this.stateLo;

    // state = old * MUL + inc (mod 2^64)
    const [prodLo, carryHi] = mul32x32(oldLo, MUL_LO);
    const prodHi = (carryHi + Math.imul(oldLo, MUL_HI) + Math.imul(oldHi, MUL_LO)) >>> 0;
    const sumLo = prodLo + this.incLo;
    this.stateLo = sumLo >>> 0;
    this.stateHi = (prodHi + this.incHi + (sumLo >= TWO_32 ? 1 : 0)) >>> 0;

    // xorshifted = uint32(((old >> 18) ^ old) >> 27); rot = old >> 59
    const xHi = ((oldHi >>> 18) ^ oldHi) >>> 0;
    const xLo = ((((oldHi << 14) | (oldLo >>> 18)) >>> 0) ^ oldLo) >>> 0;
    const xorshifted = ((xHi << 5) | (xLo >>> 27)) >>> 0;
    const rot = oldHi >>> 27;
    return ((xorshifted >>> rot) | (xorshifted << (-rot & 31))) >>> 0;
  }

  /** Uniform in [0, 1). */
  float(): number {
    return this.nextU32() / TWO_32;
  }

  /** Uniform integer in [0, n), without modulo bias. */
  int(n: number): number {
    if (!Number.isInteger(n) || n < 1 || n > TWO_32) {
      throw new RangeError(`Pcg32.int: n must be an integer in [1, 2^32], got ${n}`);
    }
    const threshold = (TWO_32 - n) % n;
    for (;;) {
      const r = this.nextU32();
      if (r >= threshold) return r % n;
    }
  }

  range(min: number, max: number): number {
    return min + this.float() * (max - min);
  }

  chance(p: number): boolean {
    return this.float() < p;
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new RangeError('Pcg32.pick: empty array');
    return items[this.int(items.length)] as T;
  }

  /** An independent generator derived from the current state and `name`; does not advance this one. */
  fork(name: string): Pcg32 {
    const hash = fnv1a(name);
    return new Pcg32((this.stateLo ^ hash) >>> 0, (this.stateHi ^ Math.imul(hash, 0x9e3779b1)) >>> 0);
  }
}
